import { QueryFunctionContext, UseInfiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { productsClient } from "@core/clients";
import { PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS } from "../../config/constansts";
import { Product } from "@core/types";

type QueryParams =
  | {
    page?: number;
    keyword?: string;
    zoneId: string;
    additionalFeature?: string;
  }
  | undefined;

type QueryKey = ["products", "search", QueryParams];
type Options = UseInfiniteQueryOptions<Product[], any, Product[], Product[], QueryKey>;

const useSearchProductsInfiniteQuery = (config: { options?: Options; params?: QueryParams } = {}) => {
  return useInfiniteQuery<Product[], any, Product[], QueryKey>({
    queryKey: ["products", "search", config.params],
    queryFn: async (queryContext: QueryFunctionContext<QueryKey, QueryParams>) => {
      const { pageParam = { page: 1 }, queryKey } = queryContext;
      const configParams = queryKey[2];

      if (!config.params?.keyword) {
        return [];
      }

      return await productsClient.searchProducts(
        {
          keyword: configParams?.keyword || "",
          pageNumber: pageParam.page!,
          additionalFeature: configParams?.additionalFeature,
        },
        config.params.zoneId,
      );
    },
    getNextPageParam: (lastRes, allData) => {
      const perPage = 10;
      const itemsAmount = allData.reduce((itemAmount: number, products: Product[]) => itemAmount + products.length, 0);

      if (lastRes.length >= perPage) {
        return {
          page: Math.ceil(itemsAmount / perPage) + 1,
          keyword: config.params?.keyword,
        };
      }

      // Writing this explicitly to indicate that if we
      // return 'undefined', then no query will be made
      return undefined;
    },
    cacheTime: PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS,
    staleTime: PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS,
    ...config.options,
  });
};

export default useSearchProductsInfiniteQuery;
